import React, {PropsWithChildren, useState, useRef, useCallback, useEffect} from 'react';
import {useKeyPress} from '../../utils/hooks/useKeyPress';
import {useOnClickOutside} from '../../utils/hooks/useOnClickOutside';

import './dropdown.scss';
import Button from '../button/Button';

export type DropdownAlignment = 'left' | 'right';
type DropdownModifiers = 'c-dropdown--fit-content' | 'is-lg' | 'is-md' | 'is-sm';

interface DropdownProps {
    button: JSX.Element;
    align?: DropdownAlignment;
    modifier?: DropdownModifiers;
    className?: string;
    hasButtons?: boolean;
    saveLabel?: string;
    cancelLabel?: string;
    onSave?: () => void;
    onCancel?: () => void;
    onOpen?: () => void;
    onClose?: () => void;
    disableDropdown?: boolean;
    menuPositionOverridden?: boolean;
    menuPositionFixed?: boolean;
    isColorReseted?: boolean;
    setIsColorReseted?: (isReset: boolean) => void;
}

function Dropdown(props: PropsWithChildren<DropdownProps>): JSX.Element {
    const {
        button,
        align = 'left',
        modifier = '',
        className = '',
        hasButtons = false,
        saveLabel = 'Save',
        cancelLabel = 'Cancel',
        onSave,
        onCancel,
        onOpen,
        onClose,
        disableDropdown = false,
        menuPositionOverridden = false,
        menuPositionFixed = false,
        children,
        isColorReseted = false,
        setIsColorReseted,
    } = props;

    const [isOpen, setIsOpen] = useState(false);
    const [isClosing, setIsClosing] = useState(false);

    const dropdownRef = useRef<HTMLDivElement>(null);

    const toggleDropdown = useCallback(
        (toggle: boolean) => {
            if (toggle === isOpen) return;

            if (toggle) {
                setIsOpen(true);
                onOpen && onOpen();
            } else {
                setIsClosing(true);
                setIsOpen(false);
                onClose && onClose();

                setTimeout(() => {
                    setIsClosing(false);
                }, 150); // 150 is animation duration set in dropdown.scss
            }
        },
        [isOpen, onClose, onOpen]
    );

    // close dropdown on "esc" press
    const isEscPressed = useKeyPress('Escape');
    useEffect(() => {
        if (isEscPressed && isOpen) toggleDropdown(false);
    }, [isEscPressed, toggleDropdown, isOpen]);

    // close dropdown if user clicks outside of it
    useOnClickOutside(dropdownRef, () => {
        if (isOpen) toggleDropdown(false);
    });

    useEffect(() => {
        if (isColorReseted && isOpen) {
            toggleDropdown(false);
            setIsColorReseted && setIsColorReseted(false);
        }
    }, [isColorReseted, isOpen, setIsColorReseted, toggleDropdown]);

    return (
        <div
            className={`c-dropdown c-dropdown--${align} ${isOpen ? 'is-open' : ''} ${
                isClosing ? 'is-closing' : ''
            } ${modifier} ${className}`}
            ref={dropdownRef}
        >
            <div
                className={`c-dropdown__button ${disableDropdown ? 'c-dropdown__button--disabled' : ''}`}
                aria-label="dropdown button"
                role="button"
                tabIndex={0}
                onClick={() => !disableDropdown && toggleDropdown(!isOpen)}
            >
                {button}
            </div>
            {!disableDropdown && (
                <div
                    className={`c-dropdown__content ${menuPositionFixed ? 'c-dropdown__content--fixed' : ''} ${
                        menuPositionOverridden ? 'c-dropdown__content--overridden' : ''
                    }`}
                >
                    {children}

                    {hasButtons && (
                        <div className="u-mt-2 u-flex u-v-center u-h-right u-gap-1">
                            <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                fullWidth
                                color="secondary"
                                onClick={() => {
                                    if (onCancel) {
                                        onCancel();
                                    }
                                    toggleDropdown(false);
                                }}
                            >
                                {cancelLabel}
                            </Button>
                            <Button
                                type="button"
                                variant="solid"
                                size="sm"
                                fullWidth
                                color="secondary"
                                onClick={() => {
                                    if (onSave) {
                                        onSave();
                                    }
                                    toggleDropdown(false);
                                }}
                            >
                                {saveLabel}
                            </Button>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

export default Dropdown;
